"use client";

import clsx from "clsx";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

const DOC_KEYS = [
  { type: "TradeLicense",               labelKey: "entityDoc1", ocrKey: "entityDoc1Ocr" },
  { type: "CertificateOfIncorporation", labelKey: "entityDoc2", ocrKey: "entityDoc2Ocr" },
  { type: "UBOChart",                   labelKey: "entityDoc3", ocrKey: "entityDoc3Ocr" },
  { type: "ProofOfAddress",             labelKey: "entityDoc4", ocrKey: "entityDoc4Ocr" },
] as const;

/**
 * Screen 1.2 — Entity registration. Drag-drop entity documents on the left;
 * Azure DI OCR auto-fills the LEI form on the right; GLEIF lookup pings.
 *
 * Modules unlocked: GLEIF + Counterparty.
 */
export function EntityRegistration({ className }: { className?: string }) {
  const t = useTranslations("t1Registration");
  const leiFields: [string, string][] = [
    [t("entityLeiLegalName"),        t("entityLeiLegalNameValue")],
    [t("entityLeiJurisdiction"),     t("entityLeiJurisdictionValue")],
    [t("entityLeiStatus"),           t("entityLeiStatusValue")],
    [t("entityLeiRegistrationAuth"), t("entityLeiRegistrationAuthValue")],
    [t("entityLeiLegalForm"),        t("entityLeiLegalFormValue")],
    [t("entityLeiRegistrationDate"), t("entityLeiRegistrationDateValue")],
  ];
  return (
    <div className={clsx("grid h-full grid-cols-2 gap-4", className)}>
      <div className="glass p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("entityStep")}</p>
        <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t("entityTitle")}</h3>
        <p className="mt-1 text-[11px] text-orbitra-teal-soft">{t("entityIntro")}</p>

        <ul className="mt-4 flex flex-col gap-2">
          {DOC_KEYS.map((d, i) => (
            <li key={d.type} className="flex items-center gap-3 rounded-md border border-state-verified/30 bg-orbitra-ink/40 px-3 py-2">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-state-verified/15 font-display text-[10px] font-semibold text-state-verified">{i + 1}</span>
              <div className="flex flex-col">
                <span className="text-[12px] font-semibold text-orbitra-paper">{t(d.labelKey)}</span>
                <span className="text-[10px] text-orbitra-muted">{t(d.ocrKey)}</span>
              </div>
              <span className="ms-auto text-[10px] font-semibold text-state-verified">{t("entityParsedTag")}</span>
            </li>
          ))}
        </ul>

        <label className="mt-3 flex items-center gap-3 rounded-md border border-state-verified/30 bg-state-verified/8 px-3 py-2 text-[12px] text-orbitra-paper">
          <input
            type="checkbox"
            defaultChecked
            className="h-4 w-4 cursor-pointer accent-[var(--state-verified)]"
          />
          {t("entityNoParentCheckbox")}
        </label>

        {/* Settlement currencies — primary + secondary, fiat and stablecoin.
         *  Display-only; the operator confirms during onboarding. */}
        <div className="mt-3 rounded-md border border-state-verified/30 bg-orbitra-ink/40 px-3 py-2">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("entitySettlementCurrencies")}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
            <div className="rounded border border-state-verified/20 bg-orbitra-ink/40 px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("entitySelectCurrency")}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="rounded border border-state-verified/40 bg-state-verified/15 px-2 py-0.5 font-mono text-[10px] text-state-verified">USD</span>
                <span className="rounded border border-state-verified/30 bg-orbitra-ink/40 px-2 py-0.5 font-mono text-[10px] text-orbitra-paper">AED</span>
                <span className="rounded border border-state-verified/30 bg-orbitra-ink/40 px-2 py-0.5 font-mono text-[10px] text-orbitra-paper">BRL</span>
              </div>
            </div>
            <div className="rounded border border-state-verified/20 bg-orbitra-ink/40 px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("entitySelectStablecoin")}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="rounded border border-state-verified/40 bg-state-verified/15 px-2 py-0.5 font-mono text-[10px] text-state-verified">USDC</span>
                <span className="rounded border border-state-verified/30 bg-orbitra-ink/40 px-2 py-0.5 font-mono text-[10px] text-orbitra-paper">USDT</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="glass flex flex-col gap-4 p-5">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("entityGleifLookup")}</p>
          <div className="mt-2 rounded-md border border-state-financed/40 bg-state-financed/10 px-3 py-2 text-[11px] text-state-financed">
            {t("entityGleifNotFound")}
          </div>
          <p className="mt-2 font-mono text-[11px] text-orbitra-teal-soft">
            {t("entityTempIdLabel")} <span className="text-orbitra-paper" dir="ltr">TMP-ALMADAR-2026-0742</span>
          </p>
          <p className="mt-1 text-[10px] text-orbitra-muted">
            {t("entityTempIdNote")}
          </p>
        </div>

        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("entityLeiForm")}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            {leiFields.map(([k, v]) => (
              <div key={k} className="rounded-md border border-state-verified/20 bg-orbitra-ink/50 px-2 py-1.5">
                <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{k}</p>
                <p className="mt-0.5 truncate text-orbitra-paper">{v}</p>
              </div>
            ))}
          </div>
        </div>

        <button type="button" onClick={advanceScreen} className="cta-primary mt-auto py-2 text-[12px]">
          {t("entityContinueCta")}
        </button>
      </div>
    </div>
  );
}
